import { createEffect, sample } from 'effector';
import { createGate } from 'effector-react';
import { createQuery } from '@farfetched/core';
import { formatPhone } from '@tools/formatPhone';
import { getResidentAccountOwner } from './api';
import type {
  GetResidentAccountOwnerPayload,
  GetResidentAccountOwnerResponse,
  Props,
  ResidentAccountOwner,
} from './types';

export const ResidentAccountOwnerControlGate = createGate<Props>();

const fxGetResidentAccountOwner = createEffect<
  GetResidentAccountOwnerPayload,
  GetResidentAccountOwnerResponse,
  Error
>().use(getResidentAccountOwner);

export const residentAccountOwnerQuery = createQuery({
  name: 'resident-account-owner',
  effect: fxGetResidentAccountOwner,
  mapData: ({ result: { personal_account_relations } }) =>
    Array.isArray(personal_account_relations)
      ? personal_account_relations.reduce((acc: ResidentAccountOwner[], item) => {
          if (item?.relation_type?.type === 'owner') {
            return [
              ...acc,
              {
                id: item?.id,
                residentGuid: item?.abstract_account_id ?? '',
                label: item?.resident?.fio ?? '',
                phone: item?.resident?.phone ? formatPhone(item.resident.phone) : '',
                email: item?.resident?.email ?? '',
              },
            ];
          }

          return acc;
        }, [])
      : [],
});

sample({
  clock: ResidentAccountOwnerControlGate.state,
  source: {
    status: ResidentAccountOwnerControlGate.status,
    state: ResidentAccountOwnerControlGate.state,
  },
  filter: ({ status, state: { accountId } }) => !!(status && accountId),
  fn: ({ state: { accountId } }) => ({ personal_account_id: accountId }),
  target: residentAccountOwnerQuery.start,
});

sample({
  clock: ResidentAccountOwnerControlGate.close,
  target: residentAccountOwnerQuery.reset,
});

sample({
  clock: residentAccountOwnerQuery.finished.success,
  source: ResidentAccountOwnerControlGate.status,
  filter: (isOpened) => !isOpened,
  target: residentAccountOwnerQuery.reset,
});
